import type { Capability } from '@simpleacs/shared';
import {
  Gauge,
  Activity,
  Boxes,
  Building2,
  Cable,
  ClipboardList,
  CreditCard,
  Cloud,
  Code2,
  Cpu,
  FileBarChart,
  FileClock,
  GitCompareArrows,
  ServerCog,
  KeyRound,
  Layers,
  LayoutDashboard,
  ListChecks,
  Network,
  Radio,
  Router,
  Server,
  Settings,
  ShieldCheck,
  Split,
  Users,
} from 'lucide-react';
import type { ComponentType } from 'react';

export interface NavItem {
  label: string;
  to: string;
  icon: ComponentType<{ className?: string }>;
  capability?: Capability;
  /** Match this route exactly, so sibling sub-paths (e.g. /settings/*) don't also activate it. */
  exact?: boolean;
  /** Open `to` in a new tab as a plain link (not an SPA route), e.g. the API docs. */
  external?: boolean;
  /** Superadmin-only entries (platform scope rather than a capability). */
  superadminOnly?: boolean;
  /** Rendered greyed out with a "soon" marker — phases 3 and 4. */
  comingSoon?: boolean;
  /** Indented under the parent; the parent expands when any child is active. */
  children?: NavItem[];
}

export interface NavSection {
  label: string;
  /** Accent (hex) for the active-item highlight, distinct per section. */
  accent: string;
  items: NavItem[];
}

/**
 * The Dashboard, which is not in a section and never folds away.
 *
 * It was the only item in an "Overview" section, which gave it a header of its
 * own and — once the menu learned to fold — the ability to be collapsed. Both
 * are wrong for the page every other page is navigated back to: a section
 * heading over a single entry is a label that says nothing, and a home link
 * you have to open a section to reach is a home link that has stopped being
 * one. It renders above the sections, always, at the same position it always
 * held.
 *
 * Its accent is DEFAULT_ACCENT — the same blue the Overview section carried,
 * and the one `accentForPath` already falls back to for a path in no section.
 * One value, so the menu entry and the page tint cannot drift.
 */
export const NAV_PRIMARY: NavItem = { label: 'Dashboard', to: '/', icon: LayoutDashboard };

export const NAV_SECTIONS: NavSection[] = [
  {
    label: 'PON',
    accent: '#10b981', // emerald
    items: [
      { label: 'ONUs', to: '/onus', icon: Radio, capability: 'device.read' },
      // Its own entry and its own top-level path, not a mode of the ONU list: a
      // bulk write is a different act from browsing, with its own plan,
      // confirmation and history — and nesting it under /onus would leave two
      // nav items lit at once with no way to tell them apart.
      { label: 'Bulk actions', to: '/bulk-actions', icon: Layers, capability: 'bulk.execute' },
      // The dispatcher's board: raise, assign and close field deployment jobs,
      // and dial the GPS-accuracy policy. field.manage, because raising a job
      // and pointing it at a person is dispatch, not field work.
      { label: 'Field jobs', to: '/field', icon: ClipboardList, capability: 'field.manage' },
      { label: 'Splitters', to: '/splitters', icon: Split, capability: 'site.read' },
      { label: 'ODF', to: '/odf', icon: Server, capability: 'site.read' },
      { label: 'Firmware', to: '/firmware', icon: Activity, capability: 'firmware.manage' },
    ],
  },
  {
    label: 'Infrastructure',
    accent: '#8b5cf6', // violet
    items: [
      // First, because it is this section's summary: every other entry below is
      // a row on it, beside the platform's own machinery.
      { label: 'Platform', to: '/infrastructure', icon: Boxes, capability: 'infra.manage', exact: true },
      { label: 'OLTs', to: '/olts', icon: Cable, capability: 'olt.read' },
      // Beside the OLTs it reads from: drift is a statement about each chassis
      // versus what this platform holds for it, reviewed fleet-first.
      { label: 'Drift', to: '/drift', icon: GitCompareArrows, capability: 'olt.read' },
      { label: 'Gateways', to: '/gateways', icon: Router, capability: 'site.read' },
      { label: 'Topology', to: '/topology', icon: Network, capability: 'site.read' },
      { label: 'GenieACS', to: '/infrastructure/genieacs', icon: ServerCog, capability: 'infra.manage' },
    ],
  },
  {
    label: 'Settings',
    accent: '#d97706', // amber
    items: [
      { label: 'Main', to: '/settings', icon: Settings, capability: 'settings.manage', exact: true },
      { label: 'TR-069 profiles', to: '/settings/tr069-profiles', icon: Cloud, capability: 'settings.manage' },
      { label: 'ONU types', to: '/onu-types', icon: Cpu, capability: 'settings.manage' },
      { label: 'Speed profiles', to: '/settings/speed-profiles', icon: Gauge, capability: 'olt.manage' },
      // One entry, two tabs (operator, 2026-08-25). Adjacent in the menu they
      // read as near-duplicates; the split that matters — what the OLT is told
      // versus what the ONU is told — is clearer on one page than in two labels.
      { label: 'Presets', to: '/settings/presets', icon: ListChecks, capability: 'olt.read' },
      // Visible to everyone in the org: when the wall is up, this is the way out.
      { label: 'Billing', to: '/billing', icon: CreditCard },
    ],
  },
  {
    label: 'Audit',
    accent: '#06b6d4', // cyan
    items: [
      { label: 'OLT log', to: '/olt-log', icon: FileBarChart, capability: 'audit.read' },
      { label: 'Action log', to: '/audit', icon: FileClock, capability: 'audit.read' },
    ],
  },
  {
    label: 'Administration',
    accent: '#f43f5e', // rose
    items: [
      /*
        `settings.manage`, not `superadminOnly`, since 2026-08-31.

        The page is where an organization's contact details are edited, and
        `PATCH /api/orgs/:id` now lets an org admin edit their own. Left
        superadmin-only, that permission was one nobody could reach: the entry
        was hidden here, and AppShell and UniversalSearch both filter on the
        same flag, so the only way in was hand-typing the URL. A capability
        nobody can exercise is worse than one that was never shipped.

        Strictly a widening — a superadmin holds `settings.manage` too. What a
        non-superadmin finds behind it is their OWN organization and nothing
        else: `GET /api/orgs` returns only their row, and every platform act on
        the page (create, suspend, delete, move equipment) still asks for
        `org.manage`, which no org role can ever hold.
      */
      {
        label: 'Organizations',
        to: '/organizations',
        icon: Building2,
        capability: 'settings.manage',
      },
      { label: 'Users', to: '/users', icon: Users, capability: 'user.manage' },
      { label: 'Roles', to: '/roles', icon: ShieldCheck, capability: 'user.manage' },
      // Grants access rather than configures the platform, which is why it sits
      // with users and roles rather than under Settings.
      { label: 'API keys', to: '/settings/api-keys', icon: KeyRound, capability: 'apikey.manage' },
      { label: 'API docs', to: '/api/docs/', icon: Code2, external: true },
    ],
  },
];

/** Fallback when a route matches no nav item (login, unknown paths). */
export const DEFAULT_ACCENT = '#3b82f6';

/**
 * The accent of the section owning `pathname` — the longest matching item wins,
 * so /settings/tr069-profiles resolves to Settings and /olts/1 to PON. Drives
 * the per-page card-header tint.
 */
export function accentForPath(pathname: string): string {
  return sectionForPath(pathname)?.accent ?? DEFAULT_ACCENT;
}

/**
 * Which section owns `pathname` — longest match wins, so
 * /settings/tr069-profiles resolves to Settings and /olts/1 to PON.
 *
 * One matcher for both questions the shell asks: which accent tints the page,
 * and which section the collapsed menu must keep open. They are the same
 * question, and two implementations would eventually disagree about a
 * sub-route — leaving a menu that highlights an item inside a section it has
 * closed.
 *
 * Children are matched too. A child's path is usually under its parent's, but
 * nothing enforces that, and a child nobody can find is worse than a slightly
 * longer loop.
 */
export function sectionForPath(pathname: string): NavSection | null {
  // The Dashboard is in no section, so it has none to keep open — and the
  // caller's `?? ` fallback is what tints it.
  if (pathname === '/') return null;
  let best: { len: number; section: NavSection } | null = null;
  for (const section of NAV_SECTIONS) {
    for (const item of section.items) {
      for (const to of [item.to, ...(item.children ?? []).map((c) => c.to)]) {
        const matches =
          to === '/' ? pathname === '/' : pathname === to || pathname.startsWith(`${to}/`);
        if (matches && (!best || to.length > best.len)) {
          best = { len: to.length, section };
        }
      }
    }
  }
  return best?.section ?? null;
}
